"use client";

import { useEffect, useState } from "react";

/**
 * SSR-safe `matchMedia` hook. Returns `false` on the server and during the
 * first client render so the markup matches; flips to the real value after
 * mount. Listens via `addEventListener('change', …)` to keep the value live
 * across resize / device-rotation without a polling loop.
 *
 * Use for layout decisions that are too dynamic for pure CSS — e.g. picking
 * between a desktop popover and a mobile sheet, or passing different prop
 * values to a JS component (like `numberOfMonths` on react-day-picker).
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia(query);
    setMatches(mql.matches);
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);

  return matches;
}
